import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { SplaReport, SplaUsageAggregate, SplaUsageRow } from '../types/models/Spla';
import type { ApiResponseSplaReportDetail, ApiResponseSplaReportList } from '../types/api/ApiResponseSpla';

interface UseAdminSplaUsageReturn {
  reports: SplaReport[];
  loading: boolean;
  /** Human-readable error message (the backend's JSend `message`), or null. */
  error: string | null;
  refetch: () => Promise<void>;
}

// GET /admin/spla/usage — the monthly report registry, newest month first. Read-only from this
// hook's perspective: the only write on the whole surface is POST …/generate (the tab calls it
// directly and refetches). Staff-only; `enabled` defers the fetch until the staff check passes.
// Branch on the JSend `body.status`, NEVER `response.ok`.
export const useAdminSplaUsage = (enabled: boolean): UseAdminSplaUsageReturn => {
  const [reports, setReports] = useState<SplaReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const auth = useAuth();
  const apiFetch = useApiFetch();

  const fetchReports = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch('/admin/spla/usage');
      const body = (await response.json().catch(() => null)) as ApiResponseSplaReportList | null;
      if (body?.status === 'success' && body.data?.reports) {
        setReports(body.data.reports);
      } else {
        setError(body?.message ?? 'Failed to load the SPLA usage reports');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching the SPLA usage reports');
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    if (enabled && auth.isAuthenticated) {
      void fetchReports();
    }
  }, [enabled, auth.isAuthenticated, fetchReports]);

  return { reports, loading, error, refetch: fetchReports };
};

export interface SplaReportDetail {
  report: SplaReport;
  aggregate: SplaUsageAggregate;
  rows: SplaUsageRow[];
}

interface UseAdminSplaUsageDetailReturn {
  detail: SplaReportDetail | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

// GET /admin/spla/usage/{id} — one month's report header + workbook-preview aggregate + frozen
// per-subject rows. `reportId` is null while no month is drilled into (no fetch fires); the
// in-tab detail view passes the selected report's id.
export const useAdminSplaUsageDetail = (
  reportId: number | null,
  enabled: boolean,
): UseAdminSplaUsageDetailReturn => {
  const [detail, setDetail] = useState<SplaReportDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const auth = useAuth();
  const apiFetch = useApiFetch();

  const fetchDetail = useCallback(async () => {
    if (reportId === null) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch(`/admin/spla/usage/${reportId}`);
      const body = (await response.json().catch(() => null)) as ApiResponseSplaReportDetail | null;
      if (body?.status === 'success' && body.data?.report && body.data.aggregate && body.data.rows) {
        setDetail({ report: body.data.report, aggregate: body.data.aggregate, rows: body.data.rows });
      } else {
        setError(body?.message ?? 'Failed to load the report detail');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching the report detail');
    } finally {
      setLoading(false);
    }
  }, [apiFetch, reportId]);

  useEffect(() => {
    // Reset so a previously-viewed month never flashes while another one loads.
    setDetail(null);
    if (enabled && reportId !== null && auth.isAuthenticated) {
      void fetchDetail();
    }
  }, [enabled, reportId, auth.isAuthenticated, fetchDetail]);

  return { detail, loading, error, refetch: fetchDetail };
};
